import { findSlotMarkers } from "../model/slots"
import type { CodecResult, ParsedMessage, ParsedMessageEntity } from "../types"
import {
  attempt,
  checkedSerialization,
  dateEntity,
  entityTree,
  failure,
  linkEntity,
  ParsedBuilder,
  quotes,
  reject,
  representability,
  type EntityNode,
} from "./shared"

type Mode = "Markdown" | "MarkdownV2"
const delimiters: Record<string, ParsedMessageEntity["type"]> = {
  "*": "bold",
  _: "italic",
  __: "underline",
  "~": "strikethrough",
  "||": "spoiler",
}
const reserved = new Set("_*[]()~`>#+-=|{}.!".split(""))

interface QuoteContext {
  expandable: boolean
}

/** Separate legacy raw-entity contexts and V2 token contexts. Escapes, fenced
 * code, destinations and blockquote prefixes never pass through a regex renderer.
 * Stable slots are lexical data atoms, resolved by the backend before Telegram. */
export function parseTelegramMarkdown(
  source: string,
  mode: Mode,
): CodecResult<ParsedMessage> {
  return attempt(() => {
    const builder = new ParsedBuilder(source, {
      kind: "markup",
      parseMode: mode,
    })
    const slots = new Map(
      findSlotMarkers(source).map((marker) => [marker.offset, marker.length]),
    )
    let i = 0
    const active: string[] = []
    function tokenAt(): string | undefined {
      if (source.startsWith("```", i)) return "```"
      if (mode === "MarkdownV2") {
        if (source.startsWith("__", i)) return "__"
        if (source.startsWith("||", i)) return "||"
        if (source.startsWith("![", i)) return "!["
      }
      return "*_`[".includes(source[i]) ||
        (mode === "MarkdownV2" && source[i] === "~")
        ? source[i]
        : undefined
    }
    function literal() {
      const length =
        slots.get(i) ?? String.fromCodePoint(source.codePointAt(i)!).length
      builder.append(source.slice(i, i + length), i, length)
      i += length
    }
    function escape(): boolean {
      if (source[i] !== "\\") return false
      const next = source[i + 1]
      if (
        next &&
        (mode === "MarkdownV2"
          ? next.charCodeAt(0) >= 1 && next.charCodeAt(0) <= 126
          : "_*`[".includes(next))
      ) {
        builder.append(next, i, 2)
        i += 2
        return true
      }
      // TDLib consumes only ASCII 1–126 escapes. All other backslashes
      // remain ordinary text, including at EOF and inside code/pre.
      return false
    }
    function rawEntity(token: string, limit: number): void {
      const start = i
      const offset = builder.text.length
      const pre = token === "```"
      i += token.length
      let language: string | undefined
      if (pre) {
        let end = i
        while (end < limit && !/[\s`]/.test(source[end])) end++
        if (end > i && end < limit && source[end] !== "`") {
          language = source.slice(i, end)
          i = end
        }
        if (source[i] === "\n" || source[i] === "\r") {
          const first = source[i++]
          if ((source[i] === "\n" || source[i] === "\r") && source[i] !== first)
            i++
        }
      }
      builder.syntax(start, i - start)
      while (i < limit && !source.startsWith(token, i)) {
        if (mode === "MarkdownV2" && escape()) continue
        if (mode === "MarkdownV2" && source[i] === "`")
          reject(
            "UNESCAPED_CODE_DELIMITER",
            "Escape backticks inside pre content",
            i,
          )
        literal()
      }
      if (i >= limit)
        reject(
          "UNCLOSED_MARKDOWN_ENTITY",
          "Unclosed code entity",
          start,
          i - start,
        )
      builder.syntax(i, token.length)
      i += token.length
      const length = builder.text.length - offset
      if (length)
        builder.entities.push(
          pre
            ? {
                type: "pre",
                offset,
                length,
                ...(language === undefined ? {} : { language }),
              }
            : { type: "code", offset, length },
        )
    }
    function destination(
      offset: number,
      length: number,
      custom: boolean,
      labelSourceStart: number,
    ): ParsedMessageEntity {
      if (source[i] !== "(") {
        if (custom)
          reject(
            "MISSING_LINK_DESTINATION",
            "Custom emoji needs a destination",
            i,
            0,
          )
        // Telegram's [https://example.org] shorthand.
        return linkEntity(
          builder.text.slice(offset, offset + length),
          offset,
          length,
          labelSourceStart,
          i - labelSourceStart - 1,
          source.slice(labelSourceStart, i - 1),
        )
      }
      builder.syntax(i++, 1)
      const start = i
      let url = ""
      while (i < source.length && source[i] !== ")") {
        const next = source[i + 1]
        if (
          mode === "MarkdownV2" &&
          source[i] === "\\" &&
          next &&
          next.charCodeAt(0) >= 1 &&
          next.charCodeAt(0) <= 126
        ) {
          url += next
          i += 2
        } else url += source[i++]
      }
      if (i === source.length)
        reject(
          "UNCLOSED_LINK_DESTINATION",
          "Unclosed link destination",
          start,
          i - start,
        )
      builder.destination(start, i - start, offset, length)
      builder.syntax(i++, 1)
      if (custom) {
        const date =
          /^tg:\/\/time\?unix=(-?[0-9]+)(?:&format=([a-zA-Z]*))?$/.exec(url)
        if (date)
          return dateEntity(
            date[1],
            date[2],
            offset,
            length,
            start,
            i - start - 1,
          )
        const match = /^tg:\/\/emoji\?id=([1-9][0-9]*)$/.exec(url)
        if (!match)
          reject(
            "UNSUPPORTED_CUSTOM_ENTITY",
            "Expected a tg://emoji?id=DECIMAL destination",
            start,
            i - start - 1,
          )
        return {
          type: "custom_emoji",
          offset,
          length,
          custom_emoji_id: match[1],
        }
      }
      return linkEntity(
        url,
        offset,
        length,
        start,
        i - start - 1,
        source.slice(start, i - 1),
      )
    }
    function quote(): void {
      const offset = builder.text.length
      builder.syntax(i++, 1)
      const context: QuoteContext = { expandable: false }
      sequence(undefined, source.length, context)
      const length = builder.text.length - offset
      if (length)
        builder.entities.push({
          type: context.expandable ? "expandable_blockquote" : "blockquote",
          offset,
          length,
        })
    }
    function sequence(
      stop?: string,
      limit = source.length,
      quoteContext?: QuoteContext,
    ): void {
      const start = i
      while (i < limit) {
        if (quoteContext && source[i] === ">" && builder.text.endsWith("\n")) {
          builder.syntax(i++, 1)
          continue
        }
        if (quoteContext && source[i] === "\n" && source[i + 1] !== ">") {
          if (stop)
            reject(
              "UNCLOSED_MARKDOWN_ENTITY",
              "An entity must close before its blockquote ends",
              start,
              i - start,
            )
          literal()
          return
        }
        if (slots.has(i)) {
          literal()
          continue
        }
        if (escape()) continue
        const token = tokenAt()
        // Underline is greedily tokenized before italic, including closers.
        if ((stop === "]" && source[i] === "]") || (stop && token === stop)) {
          builder.syntax(i, stop.length)
          i += stop.length
          return
        }
        if (
          quoteContext &&
          !stop &&
          token === "||" &&
          (i + 2 === limit || (source[i + 2] === "\n" && source[i + 3] !== ">"))
        ) {
          quoteContext.expandable = true
          builder.syntax(i, 2)
          i += 2
          continue
        }
        // Explicit zero-width separator recommended by Telegram for ___ ambiguity.
        if (
          mode === "MarkdownV2" &&
          source.startsWith("**", i) &&
          stop !== "*"
        ) {
          builder.syntax(i, 2)
          i += 2
          continue
        }
        if (
          mode === "MarkdownV2" &&
          !quoteContext &&
          source[i] === ">" &&
          (builder.text.length === 0 || builder.text.endsWith("\n"))
        ) {
          quote()
          continue
        }
        if (token === "`" || token === "```") {
          rawEntity(token, limit)
          continue
        }
        if (
          token === "[" ||
          token === "![" ||
          (token && Object.hasOwn(delimiters, token))
        ) {
          if (active.includes(token))
            reject(
              "CROSSING_MARKDOWN_ENTITY",
              "Entities must be properly nested",
              i,
              token.length,
            )
          const entityStart = i
          const offset = builder.text.length
          const link = token === "[" || token === "!["
          builder.syntax(i, token.length)
          i += token.length
          active.push(token)
          if (mode === "Markdown") {
            const close = link ? "]" : token
            while (i < limit && !source.startsWith(close, i)) literal()
            if (i === limit)
              reject(
                "UNCLOSED_MARKDOWN_ENTITY",
                "Unclosed legacy entity",
                entityStart,
                i - entityStart,
              )
            builder.syntax(i, close.length)
            i += close.length
          } else sequence(link ? "]" : token, limit, quoteContext)
          active.pop()
          const length = builder.text.length - offset
          const entity = link
            ? destination(
                offset,
                length,
                token === "![",
                entityStart + token.length,
              )
            : ({
                type: delimiters[token],
                offset,
                length,
              } as ParsedMessageEntity)
          if (length) builder.entities.push(entity)
          continue
        }
        if (mode === "MarkdownV2" && reserved.has(source[i]))
          reject(
            "UNESCAPED_MARKDOWN_CHARACTER",
            `Escape reserved character ${source[i]}`,
            i,
          )
        literal()
      }
      if (stop)
        reject(
          "UNCLOSED_MARKDOWN_ENTITY",
          "Unclosed Markdown entity",
          Math.max(0, start - stop.length),
          Math.max(1, i - start + stop.length),
        )
    }
    sequence()
    return builder.finish()
  })
}

function escapeText(
  text: string,
  mode: Mode,
  context: "text" | "code" | "legacyEntity" = "text",
): string {
  const slots = new Map(
    findSlotMarkers(text).map((marker) => [marker.offset, marker.length]),
  )
  let output = ""
  for (let i = 0; i < text.length; i++) {
    const slot = slots.get(i)
    if (slot) {
      output += text.slice(i, i + slot)
      i += slot - 1
      continue
    }
    const char = text[i]
    const needsEscape =
      mode === "MarkdownV2"
        ? context === "code"
          ? "`\\".includes(char)
          : reserved.has(char) || char === "\\"
        : context === "text" && "_*`[".includes(char)
    output += (needsEscape ? "\\" : "") + char
  }
  return output
}

/** Prefix only ordinary quoted lines, never the interior of code/pre or URLs. */
function prefixQuote(source: string): string {
  let output = ">"
  let code: "`" | "```" | undefined
  let destination = false
  for (let i = 0; i < source.length; i++) {
    const char = source[i]
    if (char === "\\") {
      output += source.slice(i, i + 2)
      i++
      continue
    }
    if (!code && source.startsWith("](", i)) {
      output += "]("
      i++
      destination = true
      continue
    }
    if (destination) {
      output += char
      if (char === ")") destination = false
      continue
    }
    if (char === "`") {
      const token = source.startsWith("```", i) ? "```" : "`"
      if (!code) code = token
      else if (token === code) code = undefined
      output += token
      i += token.length - 1
      continue
    }
    output += char
    if (char === "\n" && !code && i + 1 < source.length) output += ">"
  }
  return output
}

export function serializeTelegramMarkdown(
  parsed: ParsedMessage,
  mode: Mode,
): CodecResult<string> {
  const allowed = new Set([
    "bold",
    "italic",
    "code",
    "pre",
    "text_link",
    "mention_by_id",
    ...(mode === "MarkdownV2"
      ? [
          "underline",
          "strikethrough",
          "spoiler",
          "custom_emoji",
          "date_time",
          "blockquote",
          "expandable_blockquote",
        ]
      : []),
  ])
  const diagnostics = representability(parsed, allowed)
  if (diagnostics.length) return { ok: false, diagnostics }
  const tree = entityTree(parsed.entities)
  if (mode === "Markdown" && tree.some((node) => node.children.length))
    return failure(
      "LOSSY_CONVERSION",
      "Legacy Markdown cannot represent nested entities",
    )
  const tokens: Record<string, string> = {
    bold: "*",
    italic: "_",
    underline: "__",
    strikethrough: "~",
    spoiler: "||",
  }
  const escapeDestination = (url: string) =>
    mode === "MarkdownV2" ? url.replace(/[\\)]/g, "\\$&") : url
  function render(
    from: number,
    to: number,
    nodes: EntityNode[],
    context: "text" | "legacyEntity" = "text",
  ): string {
    let output = ""
    let cursor = from
    for (const { entity, children } of nodes) {
      output += escapeText(
        parsed.text.slice(cursor, entity.offset),
        mode,
        context,
      )
      const end = entity.offset + entity.length
      let segment: string
      if (entity.type === "code" || entity.type === "pre") {
        const text = escapeText(
          parsed.text.slice(entity.offset, end),
          mode,
          "code",
        )
        segment =
          entity.type === "pre"
            ? `\`\`\`${entity.language ?? ""}\n${text}\`\`\``
            : `\`${text}\``
      } else {
        const inner = render(
          entity.offset,
          end,
          children,
          mode === "Markdown" ? "legacyEntity" : "text",
        )
        if (entity.type === "text_link" || entity.type === "mention_by_id")
          segment = `[${inner}](${escapeDestination(entity.type === "text_link" ? entity.url : `tg://user?id=${entity.userId}`)})`
        else if (entity.type === "custom_emoji")
          segment = `![${inner}](tg://emoji?id=${entity.custom_emoji_id})`
        else if (entity.type === "date_time")
          segment = `![${inner}](tg://time?unix=${entity.unix_time}${entity.date_time_format === undefined ? "" : `&format=${entity.date_time_format}`})`
        else if (quotes.has(entity.type)) {
          // Quote line boundaries are semantic; never invent/remove newlines.
          const trailingNewline = inner.endsWith("\n")
          const body = trailingNewline ? inner.slice(0, -1) : inner
          segment =
            prefixQuote(body) +
            (entity.type === "expandable_blockquote" ? "||" : "") +
            (trailingNewline ? "\n" : "")
          if (
            output.endsWith("\n") &&
            nodes.some(
              (node) =>
                node.entity.offset + node.entity.length === entity.offset &&
                quotes.has(node.entity.type),
            )
          )
            segment = "**" + segment
        } else {
          const token = tokens[entity.type]
          // Empty bold separates an italic closer from an underline closer.
          segment =
            token +
            inner +
            (token.startsWith("_") && inner.endsWith("_") ? "**" : "") +
            token
        }
      }
      if (
        mode === "MarkdownV2" &&
        output.endsWith("_") &&
        segment.startsWith("_")
      )
        output += "**"
      output += segment
      cursor = end
    }
    return output + escapeText(parsed.text.slice(cursor, to), mode, context)
  }
  return checkedSerialization(
    parsed,
    render(0, parsed.text.length, tree),
    (source) => parseTelegramMarkdown(source, mode),
  )
}
